import IconGroup from '@central-icons-react/round-filled-radius-1-stroke-2/IconGroup3';
import {
    CardTravelOutlined as CardTravelOutlinedIcon,
    CasesOutlined as CasesOutlinedIcon,
    ContactsOutlined as ContactsOutlinedIcon,
    DomainOutlined as DomainOutlinedIcon,
    GroupsOutlined as TeamManagementIcon,
    InsightsOutlined as BiDashboardIcon,
    Shop2Outlined as Shop2OutlinedIcon,
    TuneOutlined as ControlRoomIcon,
} from '@mui/icons-material';
import {
    IconAlignment,
    IconAnalyticsDashboard,
    IconControlRoom,
    IconDashboard,
    IconGroup as NewIconGroup,
    IconHumanInWindow,
    IconJobArchitecture,
    IconJobProfile,
    IconLearning,
    IconProfile,
    IconTalentDirectory,
} from '@ui/core/icons';
import Maybe from 'true-myth/maybe';

import { route as dashboardPageRoute } from '../pages/dashboard/constants';
import { route as jobsPageRoute } from '../pages/job-list/constants';
import { route as jobProfilesPageRoute } from '../pages/job-profiles/constants';
import { route as learningOpportunitiesPageRoute } from '../pages/learning-opportunities/constants';
import { route as myGrowthPageRoute } from '../pages/my-growth/constants';
import { route as profilePageRoute } from '../pages/profile/constants';
import { route as talentDirectoryPageRoute } from '../pages/talent-directory/constants';

import type { FeatureFlagService } from '../../../../shared/source/feature-flag-service/feature-flag-service';
import type { Translate } from '../../../../shared/source/internationalization/translation';
import type { CentralIconBaseProps } from '@central-icons-react/round-filled-radius-1-stroke-2/CentralIconBase';
import type SvgIcon from '@mui/material/SvgIcon';
import type { FunctionComponent } from 'react';

type SvgIconComponent = FunctionComponent<CentralIconBaseProps> | typeof SvgIcon;

export type SideDrawerParentLinkProperties = {
    readonly children: readonly SideDrawerListItem[];
    readonly Icon: SvgIconComponent;
    readonly label: string;
};

export type SideDrawerLinkProperties = {
    readonly externalUrl: boolean;
    readonly Icon: SvgIconComponent;
    readonly label: string;
    readonly NewIcon: SvgIconComponent;
    readonly onRouteChange?: () => void;
    readonly path: string;
};

export type SideDrawerListItem = SideDrawerLinkProperties | SideDrawerParentLinkProperties;

type ListItemDependencies = {
    readonly onRouteChange?: () => void;
    readonly translate: Translate;
};

export type AnalyticsDashboardListItemDependencies = ListItemDependencies & {
    readonly canAccessBusinessIntelligence: boolean;
};

export const createAnalyticsDashboardListItem = (
    dependencies: AnalyticsDashboardListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canAccessBusinessIntelligence, onRouteChange, translate } = dependencies;

    if (canAccessBusinessIntelligence) {
        return Maybe.just({
            externalUrl: false,
            Icon: BiDashboardIcon,
            label: translate('navigation.analytics-dashboard'),
            NewIcon: IconAnalyticsDashboard,
            onRouteChange,
            path: '/business-intelligence',
        });
    }

    return Maybe.nothing();
};
export type JobProfileManagementListItemDependencies = ListItemDependencies & {
    readonly canUserGetJobProfiles: boolean;
    readonly canUserManageJobs: boolean;
};

export const createJobProfileManagementListItem = (
    dependencies: JobProfileManagementListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canUserGetJobProfiles, canUserManageJobs, onRouteChange, translate } = dependencies;

    if (canUserManageJobs || canUserGetJobProfiles) {
        const children: SideDrawerListItem[] = [];

        if (canUserManageJobs) {
            children.push({
                externalUrl: false,
                Icon: CardTravelOutlinedIcon,
                label: translate('navigation.job-postings'),
                NewIcon: IconHumanInWindow,
                onRouteChange,
                path: jobsPageRoute.patterns.jobs,
            });
        }

        if (canUserGetJobProfiles) {
            children.push({
                externalUrl: false,
                Icon: ContactsOutlinedIcon,
                label: translate('navigation.job-profiles'),
                NewIcon: IconJobProfile,
                onRouteChange,
                path: jobProfilesPageRoute.patterns.jobProfiles,
            });
        }

        return Maybe.just({
            children,
            Icon: CasesOutlinedIcon,
            label: translate('navigation.job-management'),
        });
    }

    return Maybe.nothing();
};

export type LearningManagementListItemDependencies = ListItemDependencies & {
    readonly canUserFilterLearningOpportunities: boolean;
};

export const createLearningManagementListItem = (
    dependencies: LearningManagementListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canUserFilterLearningOpportunities, onRouteChange, translate } = dependencies;

    if (canUserFilterLearningOpportunities) {
        return Maybe.just({
            externalUrl: false,
            Icon: Shop2OutlinedIcon,
            label: translate('navigation.learning-management'),
            NewIcon: IconLearning,
            onRouteChange,
            path: learningOpportunitiesPageRoute.patterns.learningOpportunities,
        });
    }

    return Maybe.nothing();
};

export type TalentDirectoryListItemDependencies = ListItemDependencies & {
    readonly canAccessTalentDirectory: boolean;
};

export const createTalentDirectoryListItem = (
    dependencies: TalentDirectoryListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canAccessTalentDirectory, onRouteChange, translate } = dependencies;

    if (canAccessTalentDirectory) {
        return Maybe.just({
            externalUrl: false,
            Icon: DomainOutlinedIcon,
            label: translate('navigation.talent-directory'),
            NewIcon: IconTalentDirectory,
            onRouteChange,
            path: talentDirectoryPageRoute.patterns.talentDirectory,
        });
    }

    return Maybe.nothing();
};

export type JobArchitectureListItemDependencies = ListItemDependencies & {
    readonly canUserGetJobArchitecture: boolean;
    readonly featureFlagService: FeatureFlagService;
};

export const createJobArchitectureListItem = (
    dependencies: JobArchitectureListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canUserGetJobArchitecture, featureFlagService, onRouteChange, translate } = dependencies;

    if (canUserGetJobArchitecture) {
        if (featureFlagService.isFeatureFlagEnabled('job-architecture-v-2')) {
            return Maybe.just({
                externalUrl: false,
                Icon: TeamManagementIcon,
                label: `${translate('navigation.job-architecture')}`,
                NewIcon: IconJobArchitecture,
                onRouteChange,
                path: '/job-architecture-v2',
            });
        }

        return Maybe.just({
            externalUrl: false,
            Icon: TeamManagementIcon,
            label: translate('navigation.job-architecture'),
            NewIcon: IconJobArchitecture,
            onRouteChange,
            path: '/job-and-skill-architecture/details',
        });
    }

    return Maybe.nothing();
};

export type FeatureFlagListItemDependencies = ListItemDependencies & {
    readonly featureFlagService: FeatureFlagService;
};

export const createPositionManagementItem = (
    dependencies: FeatureFlagListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('position-management')) {
        return Maybe.just({
            externalUrl: false,
            Icon: TeamManagementIcon,
            label: translate('navigation.position-management'),
            NewIcon: NewIconGroup,
            onRouteChange,
            path: '/position-management',
        });
    }

    return Maybe.nothing();
};

export const createAlignmentRecordItem = (dependencies: FeatureFlagListItemDependencies): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('alignment-dialogue')) {
        return Maybe.just({
            externalUrl: false,
            Icon: TeamManagementIcon,
            label: translate('navigation.my-alignment'),
            NewIcon: IconAlignment,
            onRouteChange,
            path: '/my-alignment',
        });
    }

    return Maybe.nothing();
};

export type createControlRoomListItemDependencies = ListItemDependencies & {
    readonly canUseControlRoom: boolean;
    readonly isAssumedTenant: boolean;
    readonly tenantId: Maybe<string>;
};

export const createJobArchitectureV2ListItem = (
    dependencies: FeatureFlagListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { featureFlagService, onRouteChange, translate } = dependencies;

    if (featureFlagService.isFeatureFlagEnabled('job-architecture-v-2')) {
        return Maybe.just({
            externalUrl: false,
            Icon: IconGroup,
            label: `${translate('navigation.job-architecture')}`,
            NewIcon: IconJobArchitecture,
            onRouteChange,
            path: '/job-architecture-v2',
        });
    }

    return Maybe.nothing();
};

export const createControlRoomListItem = (
    dependencies: createControlRoomListItemDependencies,
): Maybe<SideDrawerListItem> => {
    const { canUseControlRoom, isAssumedTenant, onRouteChange, tenantId, translate } = dependencies;

    if (canUseControlRoom && tenantId.isJust) {
        return Maybe.just({
            externalUrl: true,
            Icon: ControlRoomIcon,
            label: translate('navigation.control-room'),
            NewIcon: IconControlRoom,
            onRouteChange,
            path: `https://control-room.be.cobrainer.cloud/?tenantId=${tenantId.value}${isAssumedTenant ? '&assumeTenant=True' : ''}`,
        });
    }

    return Maybe.nothing();
};

export const createDashboardListItem = (dependencies: ListItemDependencies): Maybe<SideDrawerListItem> => {
    const { onRouteChange, translate } = dependencies;

    return Maybe.just({
        externalUrl: false,
        Icon: BiDashboardIcon,
        label: translate('navigation.dashboard'),
        NewIcon: IconDashboard,
        onRouteChange,
        path: dashboardPageRoute.patterns.dashboard,
    });
};

export const createProfileListItem = (dependencies: ListItemDependencies): Maybe<SideDrawerListItem> => {
    const { onRouteChange, translate } = dependencies;

    return Maybe.just({
        externalUrl: false,
        Icon: ContactsOutlinedIcon,
        label: translate('navigation.profile'),
        NewIcon: IconProfile,
        onRouteChange,
        path: profilePageRoute.patterns.usersMe,
    });
};

export const createMyGrowthListItem = (
    dependencies: ListItemDependencies & { readonly showGrowthPage: boolean },
): Maybe<SideDrawerListItem> => {
    const { onRouteChange, showGrowthPage, translate } = dependencies;

    if (!showGrowthPage) {
        return Maybe.nothing();
    }

    return Maybe.just({
        externalUrl: false,
        Icon: Shop2OutlinedIcon,
        label: translate('navigation.my-growth'),
        NewIcon: IconLearning,
        onRouteChange,
        path: myGrowthPageRoute.patterns.myGrowth,
    });
};
